import { createCn } from '@ditioas/web-ui';
import type {
  ConfigExtension,
  DefaultClassGroupIds,
  DefaultThemeGroupIds,
} from 'tailwind-merge';

/**
 * `cn` for app code, taught both the library's utilities and our own.
 *
 * tailwind-merge only knows Tailwind's own classes, so anything declared with
 * `@utility` is invisible to it: the conflicting class is kept alongside, and
 * CSS source order picks the winner. `createCn` brings the `@ditioas/web-ui`
 * utilities (the text scale, z steps, shadows, button shapes, the Radix sizing
 * classes); the config below adds the ones we declare in `src/index.css`.
 *
 * Only utilities that set a single conflicting property are registered. The
 * composites — `form-section`, `form-section-parent` (which applies through a
 * `[&>*]:` variant) and `table-link` — each set a bundle, so collapsing one
 * against a single class group would silently drop the rest.
 *
 * Registering a utility also hands it that group's conflict map, which can
 * reach further than the utility itself: `p-page-container` sits in `p`, so it
 * removes an earlier `px-*`/`py-*`/`pt-*`/… in the same call —
 * `cn('px-4 py-2', 'p-page-container')` resolves to `p-page-container` alone.
 * The same applies to the text scale, which lives in `font-size` and so drops a
 * preceding `leading-*`. For anything registered, then, the last class wins:
 * put the base first and the override last.
 *
 * That rule holds only for what is registered. For a utility the merger does
 * not know — the `--spacing-*` / `--shadow-*` `@theme` tokens, `h-action-bar`
 * and friends — both classes survive and the compiled CSS decides, and it does
 * not favour the override: `.h-action-bar` is emitted after every numeric
 * `.h-<n>`, so `cn('h-action-bar', 'h-16')` keeps both and renders the base.
 * Ordering cannot fix that; only registering the utility can.
 *
 * Register a new `@utility` here when you add it to `src/index.css`. The
 * `satisfies` makes a mistyped group key a compile error; a utility left out
 * entirely fails silently.
 */
const cn = createCn({
  extend: {
    classGroups: {
      'max-w': ['max-w-container'],
      shadow: ['even-shadow'],
      p: ['p-page-container'],
      'min-h': ['min-h-shadow-card'],
      w: ['w-planning-tool-first-col'],
    },
  },
} as const satisfies ConfigExtension<
  DefaultClassGroupIds,
  DefaultThemeGroupIds
>);

export default cn;
